import { Box, Card, CardContent, CardHeader, CircularProgress, Container, Stack, Typography } from '@mui/material';

import { useUpcomingEvents } from '@/hooks/use-public-site-modules';
import { useSiteOrgId } from '@/lib/site-org-context';
import type { CalendarBlockContent } from '@/types/database';

function formatEventDate(startAt: string, allDay: boolean): string {
  const date = new Date(startAt);
  const dateLabel = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  if (allDay) return dateLabel;
  const timeLabel = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${dateLabel} · ${timeLabel}`;
}

// Bound to real scoutingops-calendar events (see
// get_public_site_calendar_events) — never manually typed, so a unit that
// never logs in still shows next month's real campout rather than a
// hand-typed date from a year ago. Requires the unit to have opted in via
// Site Styles' Calendar block settings (sites.calendar_visible_on_site) —
// see CalendarBlockEditPanel.
export function CalendarBlockView({ content }: { content: CalendarBlockContent }) {
  const orgId = useSiteOrgId();
  const { data: events, isLoading } = useUpcomingEvents(orgId);

  return (
    <Box sx={{ py: 6, bgcolor: 'action.hover' }}>
      <Container maxWidth="md">
        {content.heading ? (
          <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 3 }}>
            {content.heading}
          </Typography>
        ) : null}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} aria-label="Loading upcoming events" />
          </Box>
        ) : (
          <Stack spacing={2}>
            {(events ?? []).map((event) => (
              <Card key={event.event_id} variant="outlined">
                <CardHeader title={event.title} subheader={formatEventDate(event.start_at, event.all_day)} />
                {event.location_name ? (
                  <CardContent sx={{ pt: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      {event.location_name}
                    </Typography>
                  </CardContent>
                ) : null}
              </Card>
            ))}
            {!events || events.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                No upcoming events scheduled yet. Check back soon.
              </Typography>
            ) : null}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
